
import { CardHangingShared, Group, Stt } from '@/domain/entities/CardHangingShared';
import { Options, Vue, prop } from 'vue-class-component';
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Checkbox from "primevue/checkbox";
import formatDate from '@/app/infrastructures/misc/common-library/v2/FormatDate';
import ConfirmAction from "../components/ConfirmAction.vue";

class Props {
  modelValue = prop< CardHangingShared[]>({
    default: () => [],
    required: true
  });
  dashboardHanging = prop<"BKD" | "OUTGOING" | string>({
    default: "BKD",
    type: String
  });
  loading = prop<boolean>({
    default: false,
    type: Boolean
  });
  skeletonCount = prop<number>({
    default: 3,
    type: Number
  });
  isCheckbox = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  components: {
    Accordion,
    AccordionTab,
    Checkbox,
    ConfirmAction
  },
  emits: ["update:modelValue", "template", "confirm", "download"]
})
class CardCollapsible extends Vue.with(Props) {
  isOpenModal = false;
  lastConfirmData = new  CardHangingShared();

  mapActivityType(type: CardHangingShared) {
    const label: any = {
      "follow_up_ops": this.$t('dashboard.cardCollapsibleHanging.followUpOps', { username: type.followUp?.opsUsername }),
      "follow_up_partner_client": this.$t(
        'dashboard.cardCollapsibleHanging.followUpPartner',
        { name: type.followUp?.partnerName, code: type.followUp?.partnerCode || type.followUp?.partnerType }
      ),
      "follow_up_username": this.$t('dashboard.cardCollapsibleHanging.followUpUser', { username: type.followUp?.username }),
      "follow_up_warehouse": this.$t('dashboard.cardCollapsibleHanging.searchSttWarehouse')
    }

    const icon: any = {
      "follow_up_ops": "ic_pic",
      "follow_up_partner_client": type.followUp?.partnerType === "client" ? "ic_followup_client" : "ic_followup_console",
      "follow_up_username": "ic_pic",
      "follow_up_warehouse": "ic_search_hanging",
    }

    return {
      label: label[type.activityType] ?? "-",
      icon: icon[type.activityType] ?? "ic_pic"
    }
  }

  mapFollowUp(type: CardHangingShared) {
    const label: any = {
      "belum": type.activityType === "follow_up_warehouse" ? this.$t('dashboard.cardCollapsibleHanging.notSearched') : this.$t('dashboard.cardCollapsibleHanging.notFollowedUp'),
      "sudah": type.activityType === "follow_up_warehouse" ? this.$t('dashboard.cardCollapsibleHanging.doneSearchStt') : this.$t('dashboard.cardCollapsibleHanging.doneFollowUp')
    }

    const icon: any = {
      "belum": type.activityType === "follow_up_airport" ? "clock-red" : "warning-red",
      "sudah": "check-circle"
    }

    const color: any = {
      "belum": "text-red-lp-2000",
      "sudah": "text-green-lp-100"
    }

    return {
      label: label[type.followUpStatus] ?? this.$t('dashboard.cardCollapsibleHanging.notFollowedUp'),
      icon: icon[type.followUpStatus] ?? "warning-red",
      color: color[type.followUpStatus] ?? "text-red-lp-2000"
    }
  }

  mapHangingDays(stt: Stt) {
    const colorMap: Record<number, string> = {
      1: "bg-aqua-lp-200 text-aqua-lp-500",
      2: "bg-aqua-lp-200 text-aqua-lp-500",
      3: "bg-aqua-lp-200 text-aqua-lp-500",
      4: "bg-aqua-lp-200 text-aqua-lp-500",
      5: "bg-yellow-lp-200 text-yellow-lp-100",
      6: "bg-orange-lp-100 text-orange-lp-200"
    };

    let color = "bg-white text-gray-lp-300";

    if (stt.hangingDays != null) {
      if (colorMap[stt.hangingDays]) {
        color = colorMap[stt.hangingDays];
      } else if (stt.hangingDays >= 7) {
        color = "bg-red-lp-2000 text-white";
      }
    }

    const label =
      stt.hangingDays != null
        ? stt.hangingDays < 7
          ? `${stt.hangingDays} ${this.$t('dashboard.cardCollapsibleHanging.days')}`
          : this.$t('dashboard.cardCollapsibleHanging.sevenOrMoreDays')
        : "-";

    return { label, color };
  }

  mapTicketStatus(stt: Stt) {
    const label: any = {
      "belum_ditemukan": this.$t('dashboard.cardCollapsibleHanging.notFound'),
      "ditemukan_konsol": this.$t('dashboard.cardCollapsibleHanging.foundConsole'),
      "ditemukan_pos": this.$t('dashboard.cardCollapsibleHanging.foundPos')
    }

    const color: any = {
      "belum_ditemukan": "bg-pink-lp-200 text-red-lp-100",
      "ditemukan_konsol": "bg-blue-lp-600 text-blue-lp-700",
      "ditemukan_pos": "bg-purple-lp-100 text-purple-lp-800"
    }

    return {
      label: label[stt.existenceStatus] ?? "-",
      color: color[stt.existenceStatus] ?? "",
      isConsole: stt.existenceStatus === "ditemukan_konsol",
      isPos: stt.existenceStatus === "ditemukan_pos"
    }
  }

  isFollowUpDone(type: CardHangingShared) {
    return type.followUpStatus === 'sudah';
  }

  onClickTemplate(data: CardHangingShared) {
    this.$emit("template", data)
  }

  onConfirmDone(data: CardHangingShared) {
    this.lastConfirmData = data;
    this.isOpenModal = true
  }

  onDownload(item: CardHangingShared) {
    const listStt = item.groups?.flatMap((group: Group) => group?.stts).map((stt : Stt) => stt?.sttNo);
    this.$emit("download", listStt)
  }

  onTabOpen(item: CardHangingShared) {
    item.isCollapsed = true;
    this.$emit("update:modelValue", this.modelValue);
  }

  onTabClose(item: CardHangingShared) {
    item.isCollapsed = false;
    this.$emit("update:modelValue", this.modelValue);
  }

  onUpdateGroup(item: Group) {
    item.isChecked = !item.isChecked;
    item.stts.forEach((stt: Stt) => {
      stt.isChecked = item.isChecked;
    })
    this.$emit("update:modelValue", this.modelValue);
  }

  onUpdateStt(stt: Stt, group: Group) {
    stt.isChecked = !stt.isChecked;
    group.isChecked = group?.stts?.every((sttItem: Stt) => sttItem.isChecked) ?? false;
    this.$emit("update:modelValue", this.modelValue);
  }

  // Format Date
  formatDateToString(date: string) {
    return formatDate(date);
  }

  async onDone() {
    this.isOpenModal = false;
    this.$emit("confirm", this.lastConfirmData)
  }

  get isDashboardOutgoing() {
    return this.dashboardHanging === 'OUTGOING';
  }

}
export default CardCollapsible;
